/**
 * Loading placeholders that keep the app's chrome visible while data or
 * code is en route. Two variants for two different moments:
 *
 * 1. AppShellSkeleton — the standalone version. Used ONLY before auth
 *    resolves, because at that point `useNav`, `useAuth`, and the selected
 *    client context aren't populated, so the real <AppShell> can't mount.
 *    Renders a static chrome silhouette (sidebar + header + topnav + content)
 *    using inline styles so it works even before global stylesheets load.
 *
 * 2. PageContentSkeleton — small content-area placeholder meant to sit
 *    INSIDE the real <AppShell>. Use it as the <Suspense> fallback for
 *    lazy page chunks: sidebar/header/topnav/footer keep working (nav
 *    still highlights, dropdowns still open) while the next page loads.
 *    This is by far the more common case.
 *
 * Route table pattern:
 *
 *   if (initialising) return <AppShellSkeleton />;
 *
 *   return (
 *     <Suspense fallback={<AppShell><PageContentSkeleton /></AppShell>}>
 *       <Routes> … </Routes>
 *     </Suspense>
 *   );
 */
import React from "react";

/* ── Tune to match AppShell's real dimensions if they drift ── */
const SIDEBAR_WIDTH = 240;
const HEADER_HEIGHT = 56;
const TOPNAV_HEIGHT = 44;
const NAV_ITEMS     = 10;
/* ─────────────────────────────────────────────────────────── */

const bg       = "var(--bg-primary, #0b0f16)";
const panel    = "var(--bg-secondary, #131a24)";
const border   = "var(--border-primary, #1f2937)";
const shimmer1 = "var(--bg-tertiary, #1a222e)";
const shimmer2 = "color-mix(in srgb, var(--bg-tertiary, #1a222e) 40%, var(--bg-secondary, #232f3d))";

/** One shimmering rectangle — self-contained, no CSS dependency. */
const Skel: React.FC<{
  w?: number | string;
  h?: number;
  radius?: number | string;
  style?: React.CSSProperties;
}> = ({ w = "100%", h = 12, radius = 4, style }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: radius,
      background: `linear-gradient(90deg, ${shimmer1} 25%, ${shimmer2} 50%, ${shimmer1} 75%)`,
      backgroundSize: "200% 100%",
      animation: "appShellShimmer 1.3s ease-in-out infinite",
      ...style,
    }}
  />
);

/** Shared shimmer keyframe — inlined so this file works without any
 *  global stylesheet being present yet. */
const ShimmerKeyframe: React.FC = () => (
  <style>{`
    @keyframes appShellShimmer {
      from { background-position: 200% 0; }
      to   { background-position: -200% 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      [style*="appShellShimmer"] { animation: none !important; }
    }
  `}</style>
);

/* ==================================================================
   1. Standalone shell skeleton — pre-auth ONLY
   ================================================================== */

export const AppShellSkeleton: React.FC = () => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `${SIDEBAR_WIDTH}px 1fr`,
      gridTemplateRows: `${HEADER_HEIGHT}px ${TOPNAV_HEIGHT}px 1fr`,
      minHeight: "100vh",
      background: bg,
    }}
  >
    {/* Sidebar spans all three rows */}
    <aside
      style={{
        gridColumn: "1 / 2",
        gridRow: "1 / 4",
        background: panel,
        borderRight: `1px solid ${border}`,
        padding: "16px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px 18px" }}>
        <Skel w={30} h={30} radius={8} />
        <Skel w={110} h={14} radius={4} />
      </div>
      {Array.from({ length: NAV_ITEMS }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 10px",
            borderRadius: 8,
          }}
        >
          <Skel w={18} h={18} radius={4} />
          <Skel w={`${45 + ((i * 13) % 40)}%`} h={12} radius={4} />
        </div>
      ))}
    </aside>

    {/* Header */}
    <header
      style={{
        gridColumn: "2 / 3",
        gridRow: "1 / 2",
        background: panel,
        borderBottom: `1px solid ${border}`,
        padding: "0 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Skel w={160} h={16} radius={4} />
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Skel w={22} h={22} radius="50%" />
        <Skel w={22} h={22} radius="50%" />
        <Skel w={100} h={16} radius={4} />
        <Skel w={32} h={32} radius="50%" />
      </div>
    </header>

    {/* TopNav */}
    <div
      style={{
        gridColumn: "2 / 3",
        gridRow: "2 / 3",
        background: panel,
        borderBottom: `1px solid ${border}`,
        padding: "0 20px",
        display: "flex",
        alignItems: "center",
        gap: 20,
      }}
    >
      {[80, 110, 90, 130, 100].map((w, i) => (
        <Skel key={i} w={w} h={12} radius={4} />
      ))}
    </div>

    {/* Content */}
    <main
      style={{
        gridColumn: "2 / 3",
        gridRow: "3 / 4",
        padding: 24,
        overflow: "hidden",
      }}
    >
      <ContentSkeletonInner />
    </main>

    <ShimmerKeyframe />
  </div>
);

/* ==================================================================
   2. Content-only skeleton — used inside the real <AppShell>
   ================================================================== */

export const PageContentSkeleton: React.FC = () => (
  <div style={{ padding: 24 }}>
    <ContentSkeletonInner />
    <ShimmerKeyframe />
  </div>
);

/* Shared content block — kept as one component so both variants stay in
 * lock-step. Generic dashboard shape: title + 4 KPI tiles + big/narrow
 * two-column body. Works for ~90% of your pages.
 */
const ContentSkeletonInner: React.FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
    <Skel w={220} h={22} radius={5} />

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 14,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            background: panel,
            border: `1px solid ${border}`,
            borderRadius: 10,
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Skel w={22} h={22} radius="50%" />
            <Skel w="60%" h={11} radius={3} />
          </div>
          <Skel w="45%" h={22} radius={5} />
        </div>
      ))}
    </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 20,
      }}
    >
      <div
        style={{
          background: panel,
          border: `1px solid ${border}`,
          borderRadius: 10,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <Skel w={180} h={14} radius={4} />
        <Skel h={220} radius={6} />
      </div>
      <div
        style={{
          background: panel,
          border: `1px solid ${border}`,
          borderRadius: 10,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <Skel w={140} h={14} radius={4} />
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Skel w={24} h={16} radius={3} />
            <Skel w={`${50 + ((i * 11) % 30)}%`} h={12} radius={4} />
            <div style={{ flex: 1 }} />
            <Skel w={36} h={12} radius={3} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default AppShellSkeleton;
